import { useEffect, useState } from "react";
import { Arrow } from "./Shared";
import { whatsapp } from "./catalog";
interface Review {
  name: string;
  testimonial: string;
  page: string;
  approved?: string;
}
const endpoint = "https://sheetdb.io/api/v1/h9n0f778sqxup";
export default function Reviews({ page }: { page: string }) {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [index, setIndex] = useState(0);
  const [writing, setWriting] = useState(false);
  const [name, setName] = useState("");
  const [feedback, setFeedback] = useState("");
  const [status, setStatus] = useState("");
  const [sending, setSending] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    fetch(endpoint, { signal: controller.signal })
      .then((r) => {
        if (!r.ok) throw Error();
        return r.json();
      })
      .then((data: Review[]) => {
        setReviews(
          data.filter(
            (r) =>
              r.page?.trim().toLowerCase() === page.toLowerCase() &&
              String(r.approved).toLowerCase() === "true" &&
              r.testimonial &&
              r.name,
          ),
        );
        setState("ready");
      })
      .catch(() => {
        if (!controller.signal.aborted) setState("error");
      });
    return () => controller.abort();
  }, [page]);
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (sending) return;
    if (!name.trim() || !feedback.trim()) {
      setStatus("Please add your name and feedback.");
      return;
    }
    setSending(true);
    setStatus("");
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          data: {
            name: name.trim(),
            testimonial: feedback.trim(),
            page,
            approved: "FALSE",
          },
        }),
      });
      if (!response.ok) throw Error();
      setStatus("Thank you. Your feedback has been submitted for review.");
      setName("");
      setFeedback("");
    } catch {
      setStatus(
        "We couldn’t submit your feedback. Your text is still here; please try again.",
      );
    } finally {
      setSending(false);
    }
  }
  const review = reviews[index];
  return (
    <section className="reviews-section">
      <div className="container reviews-inner">
        <div>
          <p className="eyebrow">SHARED EXPERIENCES</p>
          <h2>
            In their
            <br />
            <em>own words.</em>
          </h2>
          {state === "error" ? <a className="text-link" href={whatsapp("Hello, I would like to share feedback about my Radian Soul session.")} target="_blank" rel="noreferrer">Share privately with Vaanii <Arrow /></a> : <button
            className="text-link"
            onClick={() => setWriting(!writing)}
            aria-expanded={writing}
            aria-controls="feedback-form"
          >
            {writing ? "Close feedback form" : "Share your experience"}{" "}
            <Arrow />
          </button>}
        </div>
        <div className="review-content">
          {state === "loading" ? (
            <p role="status">Loading shared experiences…</p>
          ) : review ? (
            <>
              <blockquote>“{review.testimonial}”</blockquote>
              <div className="review-attribution">
                <span>
                  {review.name}
                  <small>Radian Soul client</small>
                </span>
                {reviews.length > 1 && (
                  <div className="review-controls">
                    <button
                      aria-label="Previous review"
                      onClick={() =>
                        setIndex((index - 1 + reviews.length) % reviews.length)
                      }
                    >
                      ←
                    </button>
                    <span>
                      {index + 1} / {reviews.length}
                    </span>
                    <button
                      aria-label="Next review"
                      onClick={() => setIndex((index + 1) % reviews.length)}
                    >
                      →
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <p>
              {state === "error"
                ? "Shared experiences are unavailable right now. You can share your feedback directly with Vaanii."
                : "Every journey is personal. If you have had a session with Vaanii, you’re welcome to share your experience."}
            </p>
          )}
        </div>
        {writing && state !== "error" && (
          <form className="feedback-form" id="feedback-form" onSubmit={submit}>
            <h3>Share your experience</h3>
            <p>
              Your name and feedback may be published after review. Please leave
              out private session details.
            </p>
            <div className="form-field">
              <label htmlFor="feedback-name">Your name</label>
              <input
                id="feedback-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                maxLength={100}
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="feedback-message">Your feedback</label>
              <textarea
                id="feedback-message"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={4}
                maxLength={2000}
                required
              />
            </div>
            <button className="button" disabled={sending}>
              {sending ? "Submitting…" : "Submit feedback"} <Arrow />
            </button>
            <p role="status">{status}</p>
          </form>
        )}
      </div>
    </section>
  );
}
